import BaseNetwork from "@/lib/classes/BaseNetwork";
import { derivePubkeyFromSpub } from "@/lib/helpers/derive";

class XLM extends BaseNetwork {
  constructor(xpub: string) {
    super("XLM", xpub);
  }

  private crc16Xmodem(data: Uint8Array): Uint8Array {
    let crc = 0x0000;
    for (let i = 0; i < data.length; i++) {
      crc ^= data[i] << 8;
      for (let j = 0; j < 8; j++) {
        if (crc & 0x8000) {
          crc = (crc << 1) ^ 0x1021;
        } else {
          crc <<= 1;
        }
      }
    }
    return new Uint8Array([(crc >> 0) & 0xff, (crc >> 8) & 0xff]);
  }

  private base32Encode(bytes: Uint8Array): string {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
    let bits = 0;
    let value = 0;
    let output = "";

    for (let byte of bytes) {
      value = (value << 8) | byte;
      bits += 8;
      while (bits >= 5) {
        output += alphabet[(value >>> (bits - 5)) & 31];
        bits -= 5;
      }
    }

    if (bits > 0) {
      output += alphabet[(value << (5 - bits)) & 31];
    }

    while (output.length % 8 !== 0) {
      output += "=";
    }

    return output;
  }

  private encodeStellarAddress(pubKey: Uint8Array): string {
    if (pubKey.length !== 32) {
      throw new Error("Ed25519 public key must be 32 bytes");
    }

    const versionByte = 6 << 3; // 'G' (0x30) for ED25519 public key
    const payload = new Uint8Array(1 + pubKey.length);
    payload[0] = versionByte;
    payload.set(pubKey, 1);

    const checksum = this.crc16Xmodem(payload);
    const fullPayload = new Uint8Array([...payload, ...checksum]);

    return this.base32Encode(fullPayload);
  }

  getAddress(index: number, derivationPath: number[], spub: string): string {
    const pubkey = derivePubkeyFromSpub(spub, [...derivationPath, index]);

    const pubkeyHex = Buffer.from(pubkey.derived).toString("hex");
    const pubKeyBytes = Uint8Array.from(
      pubkeyHex.match(/.{2}/g)!.map(b => parseInt(b, 16)),
    );

    const address = this.encodeStellarAddress(pubKeyBytes);

    return address;
  }
}

export default XLM;
